/**
 * 界面主题注册表（多主题配色的唯一数据源）。
 *
 * 数据来源：`docs/design/palettes/` 下的主题设计稿（`palette-1` 默认 + 各主题页）+ 现有默认主题；
 * 设计稿里的 `--primary/--ai/--grad/...` 色值在此收敛为紧凑定义，由 `applyTheme.ts`
 * 派生为完整 CSS 变量集合（alpha 档、渐变、光晕、10 档弧蓝等均在派生侧计算，避免双份维护）。
 *
 * 近邻去重（2026-09）：下线 `navy`（玄墨藏蓝）与 `celadon`（岭南瓷青）——前者与 `night-voyage`
 * 主色 OKLab ΔE 仅 5.0（差在侧栏壳层），后者与 `sand` 主色 ΔE 6.3、AI 色 ΔE 6.0（几乎同屏难辨）；
 * 补入 `terracotta-sunset`（橙相 55°）与 `wisteria-magenta`（品红 324°）两套原本空缺的色相。
 * 下线键的迁移见 `applyTheme.ts` 的 `LEGACY_THEME_KEYS`。
 *
 * 硬性约定（改动前先读 docs/design/design-spec.md §2 与 §12）：
 * 1. 功能色（成功 / 警告 / 危险 / 中性）与 `--pill-*` 状态板**不随主题变化**，
 *    因此本文件只定义主色 / AI 色 / 中性色阶 / 渐变 / 圆角 / 辉光，不出现功能色字段。
 * 2. AI 强调色只用于 AI 元素（徽标 / 发光 / 渐变 / AI 系列图表），禁止用于状态表达；
 *    与功能色同相区的主题（琥珀 / 珊瑚粉 / 鎏金）在 `caveat` 中标注需人工核对的点。
 * 3. 色值一律写在本文件（与 `utils/chartTheme.ts` 同为"色板本体"），页面与样式文件禁止再写字面量。
 */

export interface ThemeDef {
  /** 主题键：写入 localStorage 与 body[data-bcc-theme]，改动即等于换存储键 */
  key: string;
  name: string;
  en: string;
  /** 一句话气质 / 适用场景（抽屉内展示） */
  scene: string;
  /** 品牌主色（业务：按钮 / 选中 / 表头微染 / 链接） */
  primary: string;
  /** 主色按下 / hover 加深档（Arco 弧蓝 7 档同值） */
  primary2: string;
  /** AI 强调色（AI 徽标 / 发光 / 渐变终点 / AI 系列图表） */
  ai: string;
  /** AI 浅档（发光副色 / 图表浅色系列） */
  aiLight: string;
  /** AI 加深档（AI 文字小字场景，浅底上 ≥4.5:1） */
  aiDeep: string;
  /** 主渐变终点（主按钮 / logo / 左条 的第二色，比主色亮一档；「主色 → 亮档」是品牌渐变语言） */
  gradTo: string;
  /** 页面底 */
  bg: string;
  /** 侧栏渐变起止（深色壳主题填其深色壳渐变） */
  siderFrom: string;
  siderTo: string;
  /** 内容区顶部回落色 */
  bgTop: string;
  text1: string;
  text2: string;
  text3: string;
  /** 卡片圆角（按钮 / 输入 = 圆角 - 2） */
  radiusCard: number;
  /** 内容区左上 AI 色光晕强度（0~1） */
  glowAlpha: number;
  /** 主色辉光（主按钮 / logo 投影） */
  glow: string;
  /** AI 辉光（AI 徽标 / 状态点） */
  glowAi: string;
  /** 卡片 hover 浮起阴影 */
  shadowHover: string;
  /** Arco 弧蓝 1~10 档（第 6 档 = primary） */
  ramp: string[];
  /** 深色壳：侧栏文字 / 描边 / 菜单底随壳层翻转（默认继承浅色 token 的间接引用，无需填写） */
  shell?: {
    text2: string;
    text3: string;
    border: string;
    menuHoverBg: string;
    menuSelectedBg: string;
    menuHoverColor: string;
    menuSelectedColor: string;
  };
  /**
   * 已知取舍 / 评审备注 —— **内部登记项，不上界面**（界面设置抽屉只展示名称、色板与适用场景）。
   * 供设计文档与验收走查引用，
   * 例如"AI 色与功能色同相区，需形态 + AiBadge 文案区分""主色本身白字不达 AA，白字面走渐变"等。
   */
  caveat?: string;
}

/** 现有默认主题（tokens.css 现值，无覆盖；选它等于清空运行时覆盖） */
export const DEFAULT_THEME_KEY = 'default';

export const THEMES: ThemeDef[] = [
  {
    key: DEFAULT_THEME_KEY,
    name: '科技蓝青',
    en: 'DeepBlue × Cyan',
    scene: '默认标准版：深海蓝承载业务、电光青承载 AI，现代 SaaS 科技感',
    primary: '#2563eb',
    primary2: '#1e51d6',
    ai: '#06b6d4',
    aiLight: '#22d3ee',
    aiDeep: '#0e7490',
    gradTo: '#22d3ee',
    bg: '#f2f6fc',
    siderFrom: '#f8fbff',
    siderTo: '#e8f1fd',
    bgTop: '#eef5ff',
    text1: '#1d2129',
    text2: '#4e5969',
    text3: '#68717d',
    radiusCard: 10,
    glowAlpha: 0.12,
    glow: '0 4px 14px rgba(37, 99, 235, 0.26)',
    glowAi: '0 0 10px rgba(6, 182, 212, 0.45)',
    shadowHover: '0 6px 18px rgba(37, 99, 235, 0.14)',
    ramp: ['#ebf3ff', '#dbe9ff', '#add5ff', '#7cd0ff', '#4f86ff', '#2563eb', '#1e51d6', '#173fb1', '#10308c', '#0a2267'],
  },
  {
    key: 'terracotta-sunset',
    name: '落日赭橙',
    en: 'Terracotta Sunset × Dusk Indigo',
    scene: '能源 / 制造 / 民生热线：落日赭橙承载业务、深青承载 AI，暖底长时段耐看',
    primary: '#a5560e',
    primary2: '#8a470a',
    ai: '#6366f1',
    aiLight: '#a5b4fc',
    aiDeep: '#3730a3',
    gradTo: '#c48c65',
    bg: '#fbf7f3',
    siderFrom: '#fdfaf6',
    siderTo: '#f4ece3',
    bgTop: '#f8f0e8',
    text1: '#2a1c12',
    text2: '#5c4a3c',
    text3: '#7a6656',
    radiusCard: 12,
    glowAlpha: 0.09,
    glow: '0 4px 12px rgba(165, 86, 14, 0.22)',
    glowAi: '0 0 12px rgba(99, 102, 241, 0.5)',
    shadowHover: '0 5px 16px rgba(165, 86, 14, 0.14)',
    ramp: ['#f9f3ef', '#ead3c4', '#dab49a', '#c99471', '#b77546', '#a5560e', '#8a470a', '#6f3806', '#562a04', '#3e1d02'],
    caveat: '主色取橙相（H55°）而非赭红：与危险红相距 30°、与警告橙 ΔE 23.7，破坏性操作仍一律危险红；AI 用靛蓝（非青系，与成功绿相距 32°）既避开功能色相区，也在暖底上给出最强的"AI 是冷色"分层；暖底上橙色告警小字走加深档语义色',
  },
  {
    key: 'obsidian',
    name: '曜石石墨',
    en: 'Obsidian × Electric Cyan',
    scene: '演示 / POC / 标杆大屏：无彩壳把彩色预算全留给 AI，冲击力最强',
    primary: '#28323f',
    primary2: '#1b232e',
    ai: '#22d3ee',
    aiLight: '#67e8f9',
    aiDeep: '#0e7490',
    gradTo: '#22d3ee',
    bg: '#f5f6f8',
    siderFrom: '#fbfcfd',
    siderTo: '#ebeff3',
    bgTop: '#eef1f4',
    text1: '#12171e',
    text2: '#4a5361',
    text3: '#67717f',
    radiusCard: 10,
    glowAlpha: 0.16,
    glow: '0 4px 14px rgba(40, 50, 63, 0.3)',
    glowAi: '0 0 14px rgba(34, 211, 238, 0.6)',
    shadowHover: '0 6px 18px rgba(40, 50, 63, 0.16)',
    ramp: ['#f0f2f4', '#e0e4e9', '#c2cad3', '#98a3b0', '#5f6d7d', '#28323f', '#212a35', '#1a222b', '#141a21', '#0d1117'],
    caveat: '近黑主色对选中态表达力弱，且强辉光属疲劳源，不建议作全天候坐席皮肤',
  },
  {
    key: 'sand',
    name: '暖砂雾青',
    en: 'Warm Sand × Mist Teal',
    scene: '7×24 坐席 / 外包基地：暖米白底 + 低饱和主色 + 减半辉光，最耐看',
    primary: '#3f6b8c',
    primary2: '#335875',
    ai: '#3f93a6',
    aiLight: '#6fb6c6',
    aiDeep: '#2f7585',
    gradTo: '#6fc0b0',
    bg: '#faf8f4',
    siderFrom: '#fdfbf7',
    siderTo: '#f2ede3',
    bgTop: '#f6f3ec',
    text1: '#262524',
    text2: '#57544f',
    text3: '#75716c',
    radiusCard: 12,
    glowAlpha: 0.07,
    glow: '0 3px 10px rgba(63, 107, 140, 0.18)',
    glowAi: '0 0 6px rgba(63, 147, 166, 0.3)',
    shadowHover: '0 4px 12px rgba(63, 107, 140, 0.12)',
    ramp: ['#f0f4f7', '#dfe9f0', '#bcd0de', '#93b2c7', '#5d8aa8', '#3f6b8c', '#355b78', '#2b4a63', '#21384b', '#172733'],
    caveat: '橙色告警在暖白底上对比偏弱，小字告警请用加深档语义色；AI 色已向青偏移以拉开成功绿',
  },
  {
    key: 'wisteria-magenta',
    name: '紫藤品红',
    en: 'Wisteria Magenta × Chartreuse',
    scene: '新媒体 / 消费品牌 / 年轻化业务：品红主色配青柠 AI，冷暖对撞、辨识度最高',
    primary: '#a21caf',
    primary2: '#871692',
    ai: '#84cc16',
    aiLight: '#bef264',
    aiDeep: '#4d7c0f',
    gradTo: '#c372cb',
    bg: '#faf6fb',
    siderFrom: '#fcfafd',
    siderTo: '#f1e9f3',
    bgTop: '#f7eff9',
    text1: '#26182a',
    text2: '#5b4a60',
    text3: '#796a7e',
    radiusCard: 12,
    glowAlpha: 0.11,
    glow: '0 4px 14px rgba(162, 28, 175, 0.24)',
    glowAi: '0 0 10px rgba(132, 204, 22, 0.42)',
    shadowHover: '0 6px 18px rgba(162, 28, 175, 0.14)',
    ramp: ['#f9f0fa', '#eacaec', '#d9a4de', '#c87dcf', '#b654bf', '#a21caf', '#871692', '#6d0f76', '#54095b', '#3c0542'],
    caveat: '青柠 AI 本色浅底仅 1.98:1，只能作徽标 / 发光 / 渐变，AI 小字一律走 --bcc-ai-deep；它与成功绿相距 32°、与警告橙相距 52° 均不同相区，但 AI 元素仍须带 AiBadge / 文案',
  },
  {
    key: 'teal-amber',
    name: '青碧琥珀',
    en: 'Teal × Honey Gold',
    scene: '服务亲和型业务 / 内部演示：冷主色 + 暖金 AI 的反向结构',
    primary: '#0b7a6f',
    primary2: '#0a6b62',
    ai: '#a8862c',
    aiLight: '#dcb75f',
    aiDeep: '#7a5f11',
    gradTo: '#2dd4bf',
    bg: '#f3f7f5',
    siderFrom: '#f7fbf9',
    siderTo: '#eaf3ef',
    bgTop: '#eef6f3',
    text1: '#16312d',
    text2: '#5b726d',
    text3: '#60746f',
    radiusCard: 14,
    glowAlpha: 0.1,
    glow: '0 4px 12px rgba(11, 122, 111, 0.22)',
    glowAi: '0 0 10px rgba(168, 134, 44, 0.42)',
    shadowHover: '0 8px 20px rgba(11, 122, 111, 0.12)',
    ramp: ['#ecfdf9', '#d5f5ee', '#a7e8dd', '#6fd6c6', '#3ec0ae', '#0b7a6f', '#0a6b62', '#115e59', '#134e4a', '#0b3b38'],
    caveat: '暖金 AI 与警告橙同属暖区（已由同值 #f59e0b 改为 #a8862c，色差 ΔE 14.8），AI 元素仍须带 AiBadge / 文案；主渐变亮端 #2dd4bf 压白字不足（1.86:1），属品牌渐变语言的登记取舍',
  },
  {
    key: 'star-violet',
    name: '星野紫',
    en: 'Star Violet × Coral Pink',
    scene: 'AI-first / 互联网产品型客户：把"这是 AI 产品"前置表达',
    primary: '#5b45d6',
    primary2: '#4936ad',
    ai: '#ec4899',
    aiLight: '#f9a8d4',
    aiDeep: '#be185d',
    gradTo: '#a78bfa',
    bg: '#f6f5fd',
    siderFrom: '#fbfaff',
    siderTo: '#efecfb',
    bgTop: '#f1effd',
    text1: '#1c1830',
    text2: '#565073',
    text3: '#726c8b',
    radiusCard: 10,
    glowAlpha: 0.12,
    glow: '0 4px 14px rgba(91, 69, 214, 0.28)',
    glowAi: '0 0 12px rgba(236, 72, 153, 0.5)',
    shadowHover: '0 6px 18px rgba(91, 69, 214, 0.16)',
    ramp: ['#f2f0fd', '#e3dffa', '#c7bef5', '#a595ec', '#7f68e2', '#5b45d6', '#4b38b5', '#3c2d92', '#30246f', '#241a4d'],
    caveat: '珊瑚粉 AI 与危险红同处暖红区，AI 元素禁止做实心按钮，且必须带 AiBadge / 文案',
  },
  {
    key: 'ink-jade-gold',
    name: '墨玉鎏金',
    en: 'Ink Jade × Gold',
    scene: '金融 / 高端政企定制：墨玉压场、鎏金点睛，质感最重',
    primary: '#1d4f43',
    primary2: '#153b32',
    ai: '#c9992b',
    aiLight: '#e6c667',
    aiDeep: '#8a6a12',
    gradTo: '#4f8b7b',
    bg: '#f8f6f0',
    siderFrom: '#fcfbf7',
    siderTo: '#eeece2',
    bgTop: '#f5f2e9',
    text1: '#1b2320',
    text2: '#4f5a55',
    text3: '#6a726e',
    radiusCard: 12,
    glowAlpha: 0.08,
    glow: '0 4px 14px rgba(29, 79, 67, 0.24)',
    glowAi: '0 0 10px rgba(201, 153, 43, 0.42)',
    shadowHover: '0 6px 18px rgba(29, 79, 67, 0.16)',
    ramp: ['#eff4f2', '#dcebe6', '#b5d3c9', '#86b5a6', '#4f8b7b', '#1d4f43', '#164037', '#10332c', '#0b2621', '#061916'],
    caveat: '鎏金与警告琥珀同相区：AI 金只用于渐变 / 发光 / 徽标，不得用于任何状态表达',
  },
  {
    key: 'night-voyage',
    name: '夜航靛蓝',
    en: 'Night Voyage × Neon Periwinkle',
    scene: '监控中心 / 值班室 / 夜间班组：深色侧栏 + 浅色工作区',
    primary: '#24408f',
    primary2: '#1b3070',
    ai: '#7c9cff',
    aiLight: '#b9c8ff',
    aiDeep: '#3d55c9',
    gradTo: '#5c7ac4',
    bg: '#f4f6fb',
    siderFrom: '#16233c',
    siderTo: '#0e1729',
    bgTop: '#eef1f8',
    text1: '#151b2b',
    text2: '#4d5568',
    text3: '#677084',
    radiusCard: 10,
    glowAlpha: 0.1,
    glow: '0 4px 14px rgba(36, 64, 143, 0.28)',
    glowAi: '0 0 14px rgba(124, 156, 255, 0.6)',
    shadowHover: '0 6px 18px rgba(36, 64, 143, 0.16)',
    ramp: ['#eff2fa', '#dde3f4', '#bcc7e7', '#93a5d8', '#5c7ac4', '#24408f', '#1b3070', '#152858', '#0f1d40', '#091329'],
    shell: {
      text2: 'rgba(232, 238, 252, 0.72)',
      text3: 'rgba(232, 238, 252, 0.42)',
      border: 'rgba(255, 255, 255, 0.08)',
      menuHoverBg: 'rgba(124, 156, 255, 0.14)',
      menuSelectedBg: 'rgba(124, 156, 255, 0.2)',
      menuHoverColor: '#ffffff',
      menuSelectedColor: '#ffffff',
    },
    caveat: '深色只作用于侧栏；顶栏与内容区仍为浅色，避免为深底重造一套顶栏控件配色',
  },
  {
    key: 'crimson-rose',
    name: '绯樱玫瑰',
    en: 'Crimson Rose × Jade Teal',
    scene: '民生 / 社区热线 · 广电媒体客户：暖业务 + 冷 AI 的反向双核心',
    primary: '#a52a55',
    primary2: '#8a2246',
    ai: '#22a7c2',
    aiLight: '#6fcfe4',
    aiDeep: '#12687f',
    gradTo: '#cf6f92',
    bg: '#faf7f8',
    siderFrom: '#fdfbfc',
    siderTo: '#f4ebee',
    bgTop: '#f8f0f3',
    text1: '#2a1a20',
    text2: '#5f4a51',
    text3: '#816c74',
    radiusCard: 12,
    glowAlpha: 0.08,
    glow: '0 4px 12px rgba(165, 42, 85, 0.22)',
    glowAi: '0 0 8px rgba(34, 167, 194, 0.36)',
    shadowHover: '0 5px 16px rgba(165, 42, 85, 0.14)',
    ramp: ['#fbeff4', '#f6dde6', '#ecb9c9', '#dd8fa8', '#c85f80', '#a52a55', '#8a2246', '#6f1b38', '#55142b', '#3b0e1e'],
    caveat: '绯红主色与危险红同相：主色只做正向动作，破坏性操作一律危险红',
  },
];

/** 按 key 取主题（未知 key 回退默认主题，避免旧版本存储键把界面锁死） */
export function getTheme(key: string | null | undefined): ThemeDef {
  return THEMES.find((t) => t.key === key) ?? THEMES[0];
}
